import React, { useEffect, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useQuery } from '@apollo/client/react';
import { SelectDescriptor } from '@progress/kendo-react-data-tools';
import { GridHeaderSelectionChangeEvent, GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { IExtendedLibraryRiskFields } from '@pages-clean/risk-assessment/critical-to-quality-factors/critical-to-quality-factors.interface';
import { getFilteredRisks } from '@pages-clean/risk-assessment/helpers';
import { NamedItemType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { notEmpty } from '@utils/utility-helpers';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import { StudyFields, CriticalVariableFields } from '@queries/generated/fragments.generated';
import { getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk } from '@queries/generated/legacy-risk-assessment.generated';

interface AssignRiskToCtQfProps {
    onClose: () => void;
    onSubmit: (studyRiskIds: string[], close: boolean, libraryRisks: ILibraryRiskFieldsSubset[]) => void;
    onAddRiskOpen: () => void;
    study: StudyFields;
    associatedCriticalVariable: CriticalVariableFields;
    assignableRisks: IExtendedLibraryRiskFields[];
    updatingRisks: boolean;
}
export interface ICountry {
    code?: string | null;
    name?: string | null;
}

export interface ILibraryRiskFieldsSubset extends Pick<
    getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk,
    | 'responsibleFunctionId'
    | 'reviewFrequencyPeriod'
    | 'reviewFrequencyUnits'
    | 'reviewRequired'
    | 'controlStrategyId'
    | 'statement'
    | 'studyId'
    | 'vendorIds'
> {
    libraryReferenceId: string;
    categoryId: string;
    countries: ICountry[];
}

export type ISelectedRisks = {
    id: string;
    library: boolean;
};

const COMPONENT_NAME = 'assign-risks-to-ctqf-dialog';

const AssignRisksToCtqfDialog = ({
    onClose,
    onSubmit,
    onAddRiskOpen,
    study,
    associatedCriticalVariable,
    assignableRisks,
    updatingRisks,
}: AssignRiskToCtQfProps) => {
    const { confirmDiscardChanges, anyComponentHasChanges, discardChanges, setComponentHasChanges } =
        useUnsavedChangesContext();

    const { t } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors.dialogs.assignRisks' });

    const { TooltipCell, ActiveInactiveCell } = useGridCustomCell();
    const [filteredRisks, setFilteredRisks] = useState<IExtendedLibraryRiskFields[] | null>(null);
    const clearSelectedRowsEvent = useBoolean(false);
    const [select, setSelect] = React.useState<SelectDescriptor>({});

    const { data: riskCategoryData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: { type: NamedItemType.RISK_CATEGORY },
    });

    const onSubmitAssign = (close: boolean) => {
        const selectedRows = Object.keys(select).filter((k) => select[k] === true);
        const risks: ISelectedRisks[] =
            filteredRisks
                ?.filter((r) => selectedRows.indexOf(r.id) !== -1)
                .map((risk) => ({
                    id: risk.id,
                    library: risk.library,
                })) ?? [];

        const libraryRiskIds = risks.filter((r) => r.library).map((r) => r.id);
        const studyRisks = risks.filter((r) => !r.library).map((r) => r.id);

        const selectedLibraryRisks: ILibraryRiskFieldsSubset[] =
            (filteredRisks &&
                filteredRisks
                    .filter((item) => libraryRiskIds.indexOf(item.id) !== -1)
                    .map((risk: getLibraryRisks_OpraQueries_libraryRisks_LibraryRisk) => ({
                        responsibleFunctionId: risk.responsibleFunctionId,
                        reviewFrequencyPeriod: risk.reviewFrequencyPeriod,
                        reviewFrequencyUnits: risk.reviewFrequencyUnits,
                        reviewRequired: risk.reviewRequired,
                        controlStrategyId: risk.controlStrategy?.active
                            ? risk.controlStrategy.id
                            : Constants.ObjectId.Empty,
                        statement: risk.statement,
                        libraryReferenceId: risk.id,
                        categoryId: risk.riskCategory?.active ? risk.riskCategory.id : Constants.ObjectId.Empty,
                        studyId: study?.id,
                        vendorIds: risk.vendorIds,
                        countries:
                            risk.countries?.filter(notEmpty).map((country) => ({
                                code: country?.code,
                                name: country?.name,
                            })) ?? [],
                    }))) ??
            [];
        setSelect({});
        clearSelectedRowsEvent.setTrue();
        onSubmit(studyRisks, close, selectedLibraryRisks);
    };

    useEffect(() => {
        if (assignableRisks) {
            const filteredAssignableRisks = getFilteredRisks(assignableRisks);
            setFilteredRisks(filteredAssignableRisks.filteredRisks);
        }
    }, [assignableRisks]);

    const onCloseHandler = async (event?: unknown) => {
        if (anyComponentHasChanges()) {
            const confirmed = await confirmDiscardChanges();
            if (!confirmed) {
                return;
            }
            discardChanges();
        }

        const action = (event as { action?: string })?.action;
        if (action === 'button') {
            onAddRiskOpen();
        } else {
            onClose();
        }
    };

    const onSelectionChange = React.useCallback((event: GridSelectionChangeEvent) => {
        clearSelectedRowsEvent.setFalse();
        if (event.select) setSelect(event.select);
    }, []);

    const onHeaderSelectionChange = React.useCallback((event: GridHeaderSelectionChangeEvent) => {
        clearSelectedRowsEvent.setFalse();
        if (event.select) setSelect(event.select);
    }, []);

    const columns: GridColumnProps[] = [
        {
            id: 'checkbox',
            field: 'checkbox',
            columnType: 'checkbox',
            className: 'checkboxSelection',
            width: 40,
            customColumn: true,
            resizable: false,
            filterable: false,
        },
        {
            id: 'category',
            field: 'riskCategory',
            title: t('columns.category'),
            minWidth: 160,
            customColumn: true,
            filter: 'text',
            filterKey: 'name',
            lovData: riskCategoryData?.namedItemsByType
                ? riskCategoryData.namedItemsByType.map((item) => item?.name as string)
                : [],
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'statement',
            field: 'statement',
            title: t('columns.statement'),
            minWidth: 410,
            customColumn: true,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                    maxLines: 2,
                }),
            },
        },
        {
            id: 'library',
            field: 'library',
            title: t('columns.library'),
            cells: { data: YesNoCell },
            width: 20,
            filter: 'boolean',
        },
        {
            width: 1,
            minWidth: 1,
            id: 'id',
            field: 'id',
            showByDefault: false,
            title: 'ID',
        },
    ];
    const dialogTitle = `${t('title')}`;
    const formActionsEnabled = Object.keys(select).filter((k) => select[k] === true).length > 0 && !updatingRisks;
    const description = associatedCriticalVariable.description ?? '';

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, formActionsEnabled);
    }, [formActionsEnabled]);

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="assign-risks-to-ctq-factor-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => onSubmitAssign(false)}
            primaryBtnLabel={t('buttons.assign')}
            primaryBtnDisabled={!formActionsEnabled}
            secondaryBtnLabel={t('buttons.new')}
            secondaryBtnDisabled={formActionsEnabled}
            onAdditionalAction={() => onSubmitAssign(true)}
            additionalBtnLabel={t('buttons.assignAndClose')}
            additionalBtnDisabled={!formActionsEnabled}
            initialHeight={520}
            initialWidth={722}
            height={null}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} className="k-w-full k-h-full">
                <div>
                    <InfoField type="Info" message={t('infoMessage')} />
                </div>
                <div>
                    {description.length <= 600 ? (
                        <>
                            <span className="k-font-medium">{t('descriptionLabel')} - </span>{' '}
                            <span className={style.textcolor}>{description}</span>
                        </>
                    ) : (
                        <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                            <span className="k-font-medium">{t('descriptionLabel')} - </span>{' '}
                            <span title={description}>{`${description.slice(0, 600)}...`}</span>
                        </Tooltip>
                    )}
                </div>
                <div style={{ height: '300px', paddingBottom: '0.25rem' }}>
                    <Grid
                        excludeHiddenRowMenuActions
                        title={t('title')}
                        maxHeight="auto"
                        testDataId="ctqf-risks-grid"
                        data={filteredRisks}
                        columns={columns}
                        filterable
                        loading={updatingRisks}
                        useLoadingOverlay
                        showAddIcon={false}
                        scrollable="virtual"
                        dataItemKey="id"
                        selectable={{
                            enabled: true,
                            drag: false,
                            cell: false,
                            mode: 'multiple',
                        }}
                        onHeaderSelectionChange={onHeaderSelectionChange}
                        onSelectionChange={onSelectionChange}
                        clearGridSelectedRows={clearSelectedRowsEvent.value}
                        useCardWrapper={false}
                        onFilterChange={async () => {
                            clearSelectedRowsEvent.setTrue();
                            setSelect({});
                        }}
                    />
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AssignRisksToCtqfDialog;
